import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { creditService, activityService } from '../../../utils/supabaseQueries';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { LoadingOverlay } from '../../../components/ui/LoadingIndicator';

const ProjectActions = ({ credits, previewStyle, onSave, isSaving, selectedProject }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projectName, setProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleSaveProject = async () => {
    if (!projectName?.trim()) {
      return;
    }

    try {
      const projectData = {
        name: projectName,
        style: previewStyle,
        credits,
        status: 'draft'
      };

      await onSave(projectData);
      setShowSaveDialog(false);
      setProjectName('');
      setError(null);
    } catch (err) {
      setError('Failed to save project: ' + err?.message);
    }
  };

  const handleGenerateVideo = async () => {
    if (credits?.length === 0) {
      setError('Please add credits before generating video');
      return;
    }

    if (!selectedProject) {
      setError('Please save the project first');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setError(null);

    try {
      // Log activity
      await activityService?.logActivity(
        selectedProject?.id,
        'render_started',
        { creditsCount: credits?.length, style: previewStyle }
      );

      // Simulate video generation progress
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setIsGenerating(false);
            // Navigate to video rendering page with project data
            navigate('/video-rendering', { 
              state: { 
                projectId: selectedProject?.id,
                credits, 
                style: previewStyle,
                projectName: selectedProject?.name || 'Untitled Project'
              }
            });
            return 100;
          }
          return prev + Math.random() * 15;
        });
      }, 500);
    } catch (err) {
      setError('Failed to start video generation: ' + err?.message);
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const exportCredits = async () => {
    try {
      const exportData = {
        projectId: selectedProject?.id,
        projectName: selectedProject?.name || 'Film Credits',
        style: previewStyle,
        credits: credits?.map(credit => ({
          role: credit?.role,
          name: credit?.name,
          character: credit?.character || '',
          order: credit?.order
        })),
        exportedAt: new Date()?.toISOString(),
        exportedBy: user?.id
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedProject?.name || 'film-credits'}-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      window.URL?.revokeObjectURL(url);

      // Log activity
      if (selectedProject?.id) {
        await activityService?.logActivity(
          selectedProject?.id,
          'credits_exported',
          { format: 'json', creditsCount: credits?.length }
        );
      }
    } catch (err) {
      setError('Failed to export credits: ' + err?.message);
    }
  };

  const clearAll = async () => {
    if (!selectedProject) {
      // Just clear local state if no project selected
      if (window.confirm('Are you sure you want to clear all credits? This action cannot be undone.')) {
        window.location?.reload();
      }
      return;
    }

    if (window.confirm(`Are you sure you want to delete all credits from "${selectedProject?.name}"? This action cannot be undone.`)) {
      try {
        // Delete all credits from database
        const { data: existingCredits } = await creditService?.getProjectCredits(selectedProject?.id);
        if (existingCredits?.length > 0) {
          for (const credit of existingCredits) {
            await creditService?.deleteCredit(credit?.id);
          }
        }

        // Log activity
        await activityService?.logActivity(
          selectedProject?.id,
          'credits_cleared',
          { deletedCount: existingCredits?.length || 0 }
        );

        // Reload page to reflect changes
        window.location?.reload();
      } catch (err) {
        setError('Failed to clear credits: ' + err?.message);
      }
    }
  };

  // Calculate real-time stats from database
  const projectStats = {
    totalCredits: credits?.length || 0,
    uniqueRoles: new Set(credits?.map(c => c?.role))?.size || 0,
    castMembers: credits?.filter(c => c?.character)?.length || 0,
    estimatedMinutes: Math.ceil((credits?.length || 0) * 3 / 60) || 0
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Project Actions</h3>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-error/70 hover:text-error"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Project Information */}
        {selectedProject && (
          <div className="mb-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium text-foreground mb-2">Current Project</h4>
            <div className="text-sm text-muted-foreground">
              <p>Name: {selectedProject?.name}</p>
              <p>Status: {selectedProject?.status}</p>
              <p>Created: {new Date(selectedProject?.created_at)?.toLocaleDateString()}</p>
            </div>
          </div>
        )}

        {/* Project Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{projectStats?.totalCredits}</div>
            <div className="text-xs text-muted-foreground">Total Credits</div>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{projectStats?.uniqueRoles}</div>
            <div className="text-xs text-muted-foreground">Unique Roles</div>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{projectStats?.castMembers}</div>
            <div className="text-xs text-muted-foreground">Cast Members</div>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{projectStats?.estimatedMinutes}</div>
            <div className="text-xs text-muted-foreground">Est. Minutes</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          {/* Primary Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={() => setShowSaveDialog(true)}
              disabled={!selectedProject && credits?.length === 0 || isSaving}
              loading={isSaving}
              className="flex-1"
            >
              {selectedProject ? 'Update Project' : 'Save New Project'}
            </Button>
            <Button
              variant="default"
              iconName="Play"
              iconPosition="left"
              onClick={handleGenerateVideo}
              disabled={credits?.length === 0 || !selectedProject}
              className="flex-1"
            >
              Generate Video
            </Button>
          </div>

          {/* Secondary Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={exportCredits}
              disabled={credits?.length === 0}
              className="flex-1"
            >
              Export Credits
            </Button>
            <Button
              variant="outline"
              iconName="FolderOpen"
              iconPosition="left"
              onClick={() => navigate('/project-management')}
              className="flex-1"
            >
              My Projects
            </Button>
          </div>

          {/* Danger Actions */}
          <div className="pt-4 border-t border-border">
            <Button
              variant="outline"
              iconName="Trash2"
              iconPosition="left"
              onClick={clearAll}
              disabled={credits?.length === 0}
              className="w-full text-error hover:text-error hover:bg-error/10"
            >
              Clear All Credits
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-3">Quick Actions</h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => navigate('/user-dashboard')}
              className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-micro"
            >
              <Icon name="LayoutDashboard" size={16} />
              Dashboard
            </button>
            <button
              onClick={() => navigate('/video-rendering')}
              className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-micro"
            >
              <Icon name="Video" size={16} />
              Render Queue
            </button>
          </div>
        </div>
      </div>

      {/* Save Project Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-loading flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {selectedProject ? 'Update Project' : 'Save New Project'}
            </h3>
            
            <Input
              label="Project Name"
              type="text"
              placeholder="Enter project name"
              value={projectName}
              onChange={(e) => setProjectName(e?.target?.value)}
              required
              className="mb-6"
            />

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleSaveProject}
                disabled={!projectName?.trim()}
                className="flex-1"
              >
                {selectedProject ? 'Update' : 'Save'} Project
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Video Generation Loading */}
      <LoadingOverlay
        isVisible={isGenerating}
        message="Generating video credits..."
        progress={generationProgress}
        onCancel={() => {
          setIsGenerating(false);
          setGenerationProgress(0);
        }}
      />
    </>
  );
};

export default ProjectActions;